/**
 * HISIP-005: Preset Loader Modal Component
 * 
 * Modal for loading preset configurations (Cinematic, Advertiser, Educational, Animated)
 * With preview and confirmation
 */

import React, { useState } from 'react';
import { X, Zap, Eye, Film, Megaphone, GraduationCap, Shapes } from 'lucide-react';
import { cn } from '@/utils/cn';

interface PresetLoaderModalProps {
  onLoadPreset: (presetKey: string) => void;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLElement>;
}

const PRESETS = [
  {
    key: 'cinematic',
    name: 'Cinematic',
    description: 'High-contrast visuals with filmic pacing, dramatic lighting, and professional cinematography',
    icon: <Film className="w-6 h-6" />,
    color: 'purple',
    details: [
      'Film-quality visual direction',
      'Dramatic lighting and composition', 
      'Professional cinematography techniques',
      'High-contrast visual style'
    ]
  },
  {
    key: 'advertiser',
    name: 'Advertiser',
    description: 'Polished, brand-focused content with clear messaging and professional presentation',
    icon: <Megaphone className="w-6 h-6" />,
    color: 'blue',
    details: [
      'Brand-focused messaging',
      'Professional presentation style',
      'Clear value propositions',
      'Marketing-optimized content'
    ]
  },
  {
    key: 'educational',
    name: 'Educational',
    description: 'Clear, accessible content optimized for learning and knowledge retention',
    icon: <GraduationCap className="w-6 h-6" />,
    color: 'green',
    details: [
      'Learning-optimized structure',
      'Clear explanations and examples',
      'Progressive knowledge building',
      'Accessible presentation style'
    ]
  },
  {
    key: 'animated',
    name: 'Animated',
    description: 'Stylized graphics, character design, and dynamic visual storytelling',
    icon: <Shapes className="w-6 h-6" />,
    color: 'yellow',
    details: [
      'Stylized visual graphics',
      'Character-driven narratives',
      'Dynamic animation sequences',
      'Creative visual storytelling'
    ]
  }
];

const PresetLoaderModal: React.FC<PresetLoaderModalProps> = ({
  onLoadPreset,
  onClose,
  anchorRef
}) => {
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleLoadPreset = async () => {
    if (!selectedPreset) return;

    setIsLoading(true);
    try {
      await onLoadPreset(selectedPreset);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getColorClasses = (color: string) => {
    const colors = {
      purple: {
        border: 'border-purple-500/50',
        bg: 'bg-gradient-to-br from-purple-500/20 to-purple-600/20',
        text: 'text-purple-400',
        shadow: 'shadow-purple-500/10'
      },
      blue: {
        border: 'border-blue-500/50',
        bg: 'bg-gradient-to-br from-blue-500/20 to-blue-600/20',
        text: 'text-blue-400',
        shadow: 'shadow-blue-500/10'
      },
      green: {
        border: 'border-green-500/50',
        bg: 'bg-gradient-to-br from-green-500/20 to-green-600/20',
        text: 'text-green-400',
        shadow: 'shadow-green-500/10'
      },
      yellow: {
        border: 'border-yellow-500/50',
        bg: 'bg-gradient-to-br from-yellow-500/20 to-yellow-600/20',
        text: 'text-yellow-400',
        shadow: 'shadow-yellow-500/10'
      }
    };
    return colors[color as keyof typeof colors] || colors.purple;
  };

  // Compute modal position near anchor if provided
  const anchorRect = anchorRef?.current?.getBoundingClientRect();
  const style: React.CSSProperties | undefined = anchorRect
    ? { position: 'fixed', top: Math.max(20, Math.min(anchorRect.bottom - 28, window.innerHeight - 20)), right: 20, left: 'auto' }
    : undefined;

  return (
    <div 
      className="fixed inset-0 z-50 p-4 bg-black/70 backdrop-blur-sm"
      onClick={handleBackgroundClick}
    >
      <div className="w-full max-w-4xl bg-black/90 backdrop-blur-xl border border-cyan-500/30 rounded-lg shadow-2xl" style={style}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-cyan-500/20">
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-cyan-400" />
            <div>
              <h2 className="text-xl font-mono text-cyan-400 uppercase tracking-wider">
                Load Preset Configuration
              </h2>
              <p className="text-xs text-white/50 font-mono mt-1">
                Choose a preconfigured creative profile setup
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/40 hover:text-white/60 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Preset Options Grid */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {PRESETS.map((preset) => {
              const isSelected = selectedPreset === preset.key;
              const colorClasses = getColorClasses(preset.color);
              
              return (
                <div
                  key={preset.key}
                  onClick={() => setSelectedPreset(preset.key)}
                  className={cn(
                    "p-6 rounded-lg border-2 cursor-pointer transition-all duration-300",
                    "hover:transform hover:scale-[1.02]",
                    isSelected
                      ? `${colorClasses.bg} ${colorClasses.border} shadow-lg ${colorClasses.shadow}`
                      : "bg-black/30 border-white/10 hover:border-white/20"
                  )}
                >
                  <div className="flex items-start gap-4">
                    <div className={cn(
                      "p-3 rounded-lg border transition-colors",
                      isSelected
                        ? `${colorClasses.border} ${colorClasses.bg}`
                        : "border-white/20 bg-black/30"
                    )}>
                      <div className={cn(
                        "transition-colors",
                        isSelected ? colorClasses.text : "text-white/60"
                      )}>
                        {preset.icon}
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <h3 className={cn(
                        "text-lg font-mono transition-colors mb-2",
                        isSelected ? colorClasses.text : "text-white/90"
                      )}>
                        {preset.name}
                      </h3>
                      
                      <p className="text-sm text-white/70 font-mono leading-relaxed mb-4">
                        {preset.description}
                      </p>
                      
                      <ul className="space-y-1">
                        {preset.details.map((detail, index) => (
                          <li key={index} className="text-xs text-white/50 font-mono flex items-center gap-2">
                            <div className="w-1 h-1 rounded-full bg-current" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  {/* Selection indicator */}
                  {isSelected && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <div className="flex items-center gap-2">
                        <Eye className={cn("w-4 h-4", colorClasses.text)} />
                        <span className={cn("text-xs font-mono", colorClasses.text)}>
                          SELECTED FOR PREVIEW
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Warning */}
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Zap className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-red-400 font-mono text-xs mb-1">WARNING</h4>
                <p className="text-white/70 text-xs font-mono leading-relaxed">
                  Loading a preset will replace all current stage settings and allowed styles. 
                  This action cannot be undone. Make sure to save any current configuration you want to keep.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-transparent border border-white/20 rounded-lg
                       text-white/60 font-mono text-sm uppercase tracking-wider
                       hover:bg-white/5 hover:text-white/80 transition-all"
            >
              Cancel
            </button>
            
            <button
              onClick={handleLoadPreset}
              disabled={!selectedPreset || isLoading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 
                       border border-cyan-500/50 rounded-lg
                       text-cyan-400 font-mono text-sm uppercase tracking-wider
                       hover:bg-gradient-to-r hover:from-cyan-500/30 hover:to-blue-500/30
                       hover:border-cyan-500/70 transition-all
                       disabled:opacity-50 disabled:cursor-not-allowed
                       flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border border-cyan-400 border-t-transparent rounded-full animate-spin" />
                  Loading Preset...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  Load {selectedPreset ? PRESETS.find(p => p.key === selectedPreset)?.name : 'Preset'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PresetLoaderModal;